<template>
    <div ref="root" class="lazy-trip-map" :style="{ minHeight: height + 'px' }">
        <TripRouteMini v-if="visible && routes && routes.length" :routes="routes" :height="height"/>
        <div v-else-if="loading" class="d-flex align-center justify-center" :style="{ height: height + 'px' }">
            <v-progress-circular indeterminate size="22" color="primary"/>
        </div>
        <div v-else-if="loaded && (!routes || !routes.length)"
             class="text-caption text-medium-emphasis d-flex align-center justify-center"
             :style="{ height: height + 'px' }">
            No route recorded for this trip
        </div>
        <!-- Loaded with a route but scrolled off-screen: reserve the height (no map). -->
        <div v-else :style="{ height: height + 'px' }"></div>
    </div>
</template>

<script setup>
// A trip's route map for the Activity feed. Fetches its routes once (module cache)
// and mounts the Leaflet mini-map ONLY while the card is near the viewport, tearing
// it down when it scrolls away. This shows every trip's map inline (like the app)
// while capping how many live Leaflet instances / OSM tile loads exist at once and
// avoiding refetches when cards remount (filter toggles, scroll windowing).
import {ref, computed, onMounted, onBeforeUnmount} from "vue";
import {send} from "@/composables/apiClient";
import TripRouteMini from "@/components/cards/devices/TripRouteMini.vue";

const props = defineProps({
    assetId: {type: [Number, String], required: true},
    tripId: {type: [Number, String], required: true},
    height: {type: Number, default: 190},
});

// Shared across all instances so a trip's route survives unmount/remount.
const routeCache = new Map();
const cacheKey = () => `${props.assetId}-${props.tripId}`;

const root = ref(null);
// null = not loaded yet, [] = loaded but empty, [...] = loaded with a route.
const routes = ref(routeCache.has(cacheKey()) ? routeCache.get(cacheKey()) : null);
const loading = ref(false);
const visible = ref(false);
let observer = null;

const loaded = computed(() => routes.value !== null);

const load = () => {
    if (routes.value !== null || loading.value) return;
    const key = cacheKey();
    if (routeCache.has(key)) { routes.value = routeCache.get(key); return; }
    loading.value = true;
    send('get', `assets/${props.assetId}/trips/${props.tripId}/routes`)
        .then((r) => {
            const data = r?.data?.data || [];
            routeCache.set(key, data);
            routes.value = data;
        })
        // Don't cache a failure - let a later remount retry.
        .catch(() => { routes.value = []; })
        .finally(() => { loading.value = false; });
};

onMounted(() => {
    if (typeof IntersectionObserver === 'undefined') { visible.value = true; load(); return; }
    observer = new IntersectionObserver((entries) => {
        const isVis = entries.some((e) => e.isIntersecting);
        visible.value = isVis;
        if (isVis) load();
    }, {rootMargin: '400px 0px'}); // keep maps mounted within ~half a screen above/below
    observer.observe(root.value);
});

onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
});
</script>
